import { parseSearchParam } from "@/src/common/utils"
import User from "@/src/sb/components/icon/User"
import { V2_FEATURE_TOGGLES } from "@/src/sb/constants/configs"
import {
  B2C_SUNSET,
  ENABLE_EMAIL_PREFERENCES
} from "@/src/sb/constants/featureFlags"
import useRequestDemo from "@/src/sb/hooks/useRequestDemo"
import { useUser } from "@/src/sb/hooks/useUser"
import { useFeatureIsOn } from "@growthbook/growthbook-react"
import { useRouter } from "next/router"
import { useMemo } from "react"

import {
  MenuItemOption,
  MenuItemSeparator,
  accounts,
  authOptions
} from "../../constants/copy/navItems"
import NavItem from "../NavItem"
import { MenuItem } from "./MobileNavbar/MenuItem"

type AccountProps = {
  userType: keyof typeof accounts
  name: string
  onClick?: (label: string) => void
  setNextQueryParam?: boolean
}

const menuAlreadyExists = (
  menuItem: MenuItemOption,
  menuItems: MenuItemOption[]
) =>
  menuItems.some((item) => {
    if ("type" in item && item.type === "separator") return false
    if ("type" in menuItem && menuItem.type === "separator") return false
    const linkItem = item as Exclude<typeof item, MenuItemSeparator>
    const linkMenuItem = menuItem as Exclude<typeof menuItem, MenuItemSeparator>
    return linkItem.label === linkMenuItem.label
  })

export const Accounts = (props: AccountProps) => {
  const { asPath = "" } = useRouter()
  const { userType, name, onClick, setNextQueryParam } = props
  const requestDemo = useRequestDemo()
  const user = useUser()
  const emailPreferencesEnabled = useFeatureIsOn(ENABLE_EMAIL_PREFERENCES)

  const HAS_PRO_ROLE = user?.data?.roles?.includes("pro")

  const requestDemoMenuItem = {
    src: "/assets/icons/navItems/upgrade.svg",
    href: requestDemo.url,
    label: "Request Demo",
    canHaveNextQueryParam: true,
    handleClick: () =>
      requestDemo.trackClickCorporateQuote({ context: "navbar" })
  }

  const menuItems: MenuItemOption[] = useMemo(() => {
    let items: MenuItemOption[] = accounts[userType]
      ? [...accounts[userType]]
      : []

    if (!HAS_PRO_ROLE) {
      items = items.filter((item) => {
        if ("type" in item && item.type === "separator") return true
        const linkItem = item as Exclude<typeof item, MenuItemSeparator>
        return linkItem.label !== "Switch to Stears Pro"
      })
    }

    if (!emailPreferencesEnabled) {
      items = items.filter((item) => {
        if ("type" in item && item.type === "separator") return true
        const linkItem = item as Exclude<typeof item, MenuItemSeparator>
        return linkItem.label !== authOptions.emailPreferences.label
      })
    }

    if (userType === "loggedIn" && !requestDemo.loading) {
      let menuItemToAdd = authOptions.upgrade
      if (V2_FEATURE_TOGGLES.has(B2C_SUNSET)) {
        menuItemToAdd = requestDemoMenuItem
      }
      if (!menuAlreadyExists(menuItemToAdd, items)) {
        const insertIndex = items.length - 1
        items = [
          ...items.slice(0, insertIndex),
          menuItemToAdd,
          ...items.slice(insertIndex)
        ]
      }
    }

    return items
  }, [userType, requestDemo.loading, HAS_PRO_ROLE, emailPreferencesEnabled])

  return (
    <NavItem.Dropdown
      type="rounded-gray"
      label={name}
      menuAlign="right"
      icon={
        <span className="p-1 dark:p-0 bg-gray-200 dark:bg-transparent rounded-full text-black dark:text-stears_white">
          <User />
        </span>
      }
    >
      <MenuItem
        menuItems={menuItems.map((item) => {
          if (!("href" in item)) {
            return item
          }
          return {
            ...item,
            href:
              setNextQueryParam &&
              "canHaveNextQueryParam" in item &&
              item.canHaveNextQueryParam
                ? `${item.href}?${parseSearchParam({
                    next: asPath
                  }).toString()}`
                : item.href
          }
        })}
        onClick={onClick}
        trackingContext="desktop_navbar"
      />
    </NavItem.Dropdown>
  )
}
